import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea } from "@/components/voice-field";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Inbox as InboxIcon,
  Send,
  FileText,
  Mail,
  MailOpen,
  Reply,
  AlertCircle,
  Loader2,
  Save,
  Trash2,
  RefreshCw,
  PenSquare,
  ArrowLeft,
  X as XIcon,
  Users,
  Plus,
  Link2,
  Unlink,
} from "lucide-react";
import { format } from "date-fns";
import DOMPurify from "dompurify";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { PlatformLayout } from "./platform-layout";

// ─── types ────────────────────────────────────────────────────────────────────

type GmailStatus = { connected: boolean; email: string | null };

type GmailListItem = {
  id: string;
  threadId: string;
  subject: string;
  from?: string;
  to?: string;
  snippet: string;
  date: string | null;
  unread?: boolean;
};

type GmailDraftItem = {
  draftId: string;
  messageId: string;
  threadId: string;
  subject: string;
  to: string;
  snippet: string;
  date: string | null;
};

type GmailDraftDetail = {
  draftId: string;
  messageId: string;
  threadId: string;
  subject: string;
  to: string;
  cc: string;
  bcc: string;
  from: string;
  date: string | null;
  bodyHtml: string;
  bodyPlainText: string;
};

type GmailMessageDetail = {
  id: string;
  subject: string;
  from: string;
  to: string;
  cc: string;
  date: string | null;
  bodyHtml: string;
  bodyPlainText: string;
  threadId?: string;
};

type Folder = "inbox" | "drafts" | "sent" | "compose" | "mailboxes";

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatDate(d: string | null): string {
  if (!d) return "";
  try {
    return format(new Date(d), "MMM d");
  } catch {
    return "";
  }
}

function stripAngle(addr: string): string {
  if (!addr) return "";
  const m = addr.match(/^([^<]+)<([^>]+)>$/);
  if (m) return m[1].trim().replace(/^["']|["']$/g, "") || m[2].trim();
  return addr;
}

function htmlToPlain(html: string): string {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>(\r?\n)?/gi, "\n")
    .replace(/<\/(p|div|h[1-6]|li)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// ─── folder tabs config ───────────────────────────────────────────────────────

const FOLDERS: Array<{ key: Folder; label: string; icon: typeof InboxIcon }> = [
  { key: "inbox", label: "Inbox", icon: InboxIcon },
  { key: "drafts", label: "Drafts", icon: FileText },
  { key: "sent", label: "Sent", icon: Send },
  { key: "compose", label: "Compose", icon: PenSquare },
  { key: "mailboxes", label: "Shared Mailboxes", icon: Users },
];

// ─── Gmail Connect Prompt ─────────────────────────────────────────────────────

function GmailConnectPrompt({ onConnect }: { onConnect: () => void }) {
  return (
    <Card className="flex flex-col items-center justify-center p-10 text-center gap-4 h-full">
      <Mail className="w-12 h-12 text-muted-foreground opacity-60" />
      <div>
        <p className="font-semibold text-lg">Connect your Gmail</p>
        <p className="text-sm text-muted-foreground mt-1">
          Link your personal Gmail account to read and send emails from the platform.
        </p>
      </div>
      <Button onClick={onConnect} data-testid="button-connect-gmail">
        Connect Gmail
      </Button>
    </Card>
  );
}

// ─── Not-connected inline notice ─────────────────────────────────────────────

function NotConnectedNotice({ label }: { label: string }) {
  return (
    <div className="p-6 text-sm text-muted-foreground flex items-start gap-2">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>Connect your Gmail to view {label}.</span>
    </div>
  );
}

// ─── Inbox Panel ─────────────────────────────────────────────────────────────

function InboxPanel() {
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState("");

  const listQuery = useQuery<{ messages: GmailListItem[] }>({
    queryKey: ["/api/platform/gmail/inbox"],
    retry: false,
  });

  const messages = listQuery.data?.messages || [];
  const selectedMsg = messages.find((m) => m.id === selectedId) || null;

  const detailQuery = useQuery<{ message: GmailMessageDetail }>({
    queryKey: ["/api/platform/gmail/messages", selectedId],
    enabled: !!selectedId,
    retry: false,
  });

  const listError = listQuery.error as any;
  const notConnected = listError?.message?.includes("not connected") || listError?.status === 401 || listError?.status === 403;

  const replyMutation = useMutation({
    mutationFn: async () => {
      const msg = detailQuery.data?.message;
      if (!msg) throw new Error("No message loaded");
      const res = await apiRequest("POST", "/api/platform/gmail/reply", {
        threadId: msg.threadId || selectedMsg?.threadId,
        inReplyToMessageId: null,
        to: msg.from,
        subject: msg.subject,
        body: replyBody,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Reply sent" });
      setReplyOpen(false);
      setReplyBody("");
      queryClient.invalidateQueries({ queryKey: ["/api/platform/gmail/sent"] });
    },
    onError: (err: any) => {
      toast({ title: "Send failed", description: err?.message || "Could not send reply", variant: "destructive" });
    },
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4 h-full">
      {/* List pane */}
      <Card className="flex flex-col overflow-hidden" data-testid="platform-inbox-list">
        <div className="px-4 py-3 border-b flex items-center gap-2">
          <InboxIcon className="w-4 h-4" />
          <span className="font-semibold">Inbox</span>
          {!listQuery.isLoading && (
            <span className="text-xs text-muted-foreground ml-auto">{messages.length}</span>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="w-7 h-7 ml-1"
            onClick={() => listQuery.refetch()}
            disabled={listQuery.isFetching}
            data-testid="button-refresh-inbox"
          >
            <RefreshCw className={cn("w-3.5 h-3.5", listQuery.isFetching && "animate-spin")} />
          </Button>
        </div>
        <ScrollArea className="flex-1">
          {listQuery.isLoading ? (
            <div className="p-4 space-y-3">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : notConnected ? (
            <NotConnectedNotice label="inbox" />
          ) : messages.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">Your inbox is empty.</div>
          ) : (
            <div className="divide-y">
              {messages.map((m) => (
                <button
                  key={m.id}
                  data-testid={`inbox-item-${m.id}`}
                  onClick={() => { setSelectedId(m.id); setReplyOpen(false); setReplyBody(""); }}
                  className={cn(
                    "w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors",
                    selectedId === m.id && "bg-muted/60",
                  )}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className={cn("text-xs truncate flex-1", m.unread ? "font-semibold text-foreground" : "text-muted-foreground")}>
                      {m.from ? stripAngle(m.from) : "(no sender)"}
                    </span>
                    <span className="text-[11px] text-muted-foreground shrink-0">{formatDate(m.date)}</span>
                  </div>
                  <div className={cn("text-sm truncate", m.unread ? "font-semibold" : "")}>{m.subject || "(no subject)"}</div>
                  <div className="text-xs text-muted-foreground truncate">{m.snippet}</div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </Card>

      {/* Detail pane */}
      <Card className="flex flex-col overflow-hidden" data-testid="platform-inbox-detail">
        {!selectedId ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            Select a message to read it
          </div>
        ) : detailQuery.isLoading ? (
          <div className="p-6 space-y-3">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : detailQuery.error || !detailQuery.data?.message ? (
          <div className="p-6 text-sm text-destructive">Failed to load message.</div>
        ) : (
          <>
            <div className="px-6 py-4 border-b space-y-1 shrink-0">
              <div className="text-lg font-semibold" data-testid="text-inbox-subject">
                {detailQuery.data.message.subject || "(no subject)"}
              </div>
              <div className="text-xs text-muted-foreground">
                From: {stripAngle(detailQuery.data.message.from)}
                {detailQuery.data.message.cc ? ` · Cc: ${stripAngle(detailQuery.data.message.cc)}` : ""}
                {detailQuery.data.message.date ? ` · ${formatDate(detailQuery.data.message.date)}` : ""}
              </div>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-6">
                {detailQuery.data.message.bodyHtml ? (
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(detailQuery.data.message.bodyHtml, {
                        USE_PROFILES: { html: true },
                        FORBID_TAGS: ["style", "iframe", "object", "embed", "base", "meta", "form"],
                      }),
                    }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm">{detailQuery.data.message.bodyPlainText}</pre>
                )}
              </div>

              {/* Reply area */}
              {replyOpen ? (
                <div className="mx-6 mb-6 border rounded-lg p-4 space-y-3" data-testid="panel-reply">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      Replying to {stripAngle(detailQuery.data.message.from)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-7 h-7"
                      onClick={() => { setReplyOpen(false); setReplyBody(""); }}
                      data-testid="button-close-reply"
                    >
                      <XIcon className="w-4 h-4" />
                    </Button>
                  </div>
                  <VoiceToTextTextarea
                    rows={6}
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Write your reply…"
                    className="font-mono text-sm"
                    data-testid="input-reply-body"
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setReplyOpen(false); setReplyBody(""); }}
                      data-testid="button-cancel-reply"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => replyMutation.mutate()}
                      disabled={replyMutation.isPending || !replyBody.trim()}
                      data-testid="button-send-reply"
                    >
                      {replyMutation.isPending ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending…</>
                      ) : (
                        <><Send className="w-4 h-4 mr-1" /> Send Reply</>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="px-6 pb-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setReplyOpen(true)}
                    data-testid="button-open-reply"
                  >
                    <Reply className="w-4 h-4 mr-1" /> Reply
                  </Button>
                </div>
              )}
            </ScrollArea>
          </>
        )}
      </Card>
    </div>
  );
}

// ─── Sent / Drafts Panel (shared) ────────────────────────────────────────────

function GmailFolderPanel({ folder }: { folder: "sent" | "drafts" }) {
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listKey = folder === "sent" ? "/api/platform/gmail/sent" : "/api/platform/gmail/drafts";
  const listQuery = useQuery<{ messages?: GmailListItem[]; drafts?: GmailDraftItem[] }>({
    queryKey: [listKey],
    retry: false,
  });

  const items: Array<{ id: string; messageId: string; subject: string; counterparty: string; snippet: string; date: string | null }> =
    folder === "sent"
      ? (listQuery.data?.messages || []).map((m) => ({
          id: m.id,
          messageId: m.id,
          subject: m.subject || "(no subject)",
          counterparty: m.to ? `To: ${stripAngle(m.to)}` : "",
          snippet: m.snippet,
          date: m.date,
        }))
      : (listQuery.data?.drafts || []).map((d) => ({
          id: d.draftId,
          messageId: d.messageId,
          subject: d.subject || "(no subject)",
          counterparty: d.to ? `To: ${stripAngle(d.to)}` : "(no recipient)",
          snippet: d.snippet,
          date: d.date,
        }));

  const selectedItem = items.find((i) => i.id === selectedId) || null;
  const listError = listQuery.error as any;
  const notConnected =
    listError?.message?.includes("not connected") || listError?.status === 401 || listError?.status === 403;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4 h-full">
      <Card className="flex flex-col overflow-hidden" data-testid={`platform-${folder}-list`}>
        <div className="px-4 py-3 border-b flex items-center gap-2">
          {folder === "sent" ? <Mail className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
          <span className="font-semibold capitalize">{folder}</span>
          {!listQuery.isLoading && (
            <span className="text-xs text-muted-foreground ml-auto">{items.length}</span>
          )}
        </div>
        <ScrollArea className="flex-1">
          {listQuery.isLoading ? (
            <div className="p-4 space-y-3">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : notConnected ? (
            <NotConnectedNotice label={folder} />
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">
              No {folder === "sent" ? "sent messages" : "drafts"} yet.
            </div>
          ) : (
            <div className="divide-y">
              {items.map((it) => (
                <button
                  key={it.id}
                  data-testid={`${folder}-item-${it.id}`}
                  onClick={() => setSelectedId(it.id)}
                  className={cn(
                    "w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors",
                    selectedId === it.id && "bg-muted/60",
                  )}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs text-muted-foreground truncate flex-1">{it.counterparty}</span>
                    <span className="text-[11px] text-muted-foreground shrink-0">{formatDate(it.date)}</span>
                  </div>
                  <div className="text-sm font-medium truncate">{it.subject}</div>
                  <div className="text-xs text-muted-foreground truncate">{it.snippet}</div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </Card>

      <Card className="flex flex-col overflow-hidden" data-testid={`platform-${folder}-detail`}>
        {!selectedItem ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            Select a {folder === "sent" ? "message" : "draft"} to view it
          </div>
        ) : folder === "sent" ? (
          <SentDetail messageId={selectedItem.messageId} />
        ) : (
          <DraftEditor
            draftId={selectedItem.id}
            onClose={() => setSelectedId(null)}
            toast={toast}
          />
        )}
      </Card>
    </div>
  );
}

// ─── Sent Detail ─────────────────────────────────────────────────────────────

function SentDetail({ messageId }: { messageId: string }) {
  const detailQuery = useQuery<{ message: GmailMessageDetail }>({
    queryKey: ["/api/platform/gmail/messages", messageId],
    enabled: !!messageId,
    retry: false,
  });

  if (detailQuery.isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (detailQuery.error) return <div className="p-6 text-sm text-destructive">Failed to load message.</div>;
  const msg = detailQuery.data?.message;
  if (!msg) return null;
  return (
    <>
      <div className="px-6 py-4 border-b space-y-1 shrink-0">
        <div className="text-lg font-semibold" data-testid="text-sent-subject">{msg.subject || "(no subject)"}</div>
        <div className="text-xs text-muted-foreground">
          To: {stripAngle(msg.to)}
          {msg.cc ? ` · Cc: ${stripAngle(msg.cc)}` : ""}
          {msg.date ? ` · ${formatDate(msg.date)}` : ""}
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-6">
          {msg.bodyHtml ? (
            <div
              className="prose prose-sm dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(msg.bodyHtml, {
                  USE_PROFILES: { html: true },
                  FORBID_TAGS: ["style", "iframe", "object", "embed", "base", "meta", "form"],
                }),
              }}
            />
          ) : (
            <pre className="whitespace-pre-wrap text-sm">{msg.bodyPlainText}</pre>
          )}
        </div>
      </ScrollArea>
    </>
  );
}

// ─── Draft Editor ─────────────────────────────────────────────────────────────

function DraftEditor({
  draftId,
  onClose,
  toast,
}: {
  draftId: string;
  onClose: () => void;
  toast: ReturnType<typeof useToast>["toast"];
}) {
  const detailQuery = useQuery<{ draft: GmailDraftDetail }>({
    queryKey: ["/api/platform/gmail/drafts", draftId],
    enabled: !!draftId,
    retry: false,
  });

  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);

  useEffect(() => {
    const d = detailQuery.data?.draft;
    if (d) {
      setTo(d.to || "");
      setCc(d.cc || "");
      setBcc(d.bcc || "");
      setSubject(d.subject || "");
      setBody(d.bodyPlainText || htmlToPlain(d.bodyHtml || ""));
      setShowCcBcc(!!(d.cc || d.bcc));
    }
  }, [detailQuery.data?.draft]);

  const invalidateAfter = (alsoSent = false) => {
    queryClient.invalidateQueries({ queryKey: ["/api/platform/gmail/drafts"] });
    if (alsoSent) queryClient.invalidateQueries({ queryKey: ["/api/platform/gmail/sent"] });
  };

  const saveDraft = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", `/api/platform/gmail/drafts/${draftId}`, { to, cc, bcc, subject, body });
      return res.json();
    },
    onSuccess: () => {
      invalidateAfter();
      queryClient.invalidateQueries({ queryKey: ["/api/platform/gmail/drafts", draftId] });
      toast({ title: "Draft saved" });
    },
    onError: (err: any) => toast({ title: "Save failed", description: err?.message, variant: "destructive" }),
  });

  const sendDraft = useMutation({
    mutationFn: async () => {
      await apiRequest("PUT", `/api/platform/gmail/drafts/${draftId}`, { to, cc, bcc, subject, body });
      const res = await apiRequest("POST", `/api/platform/gmail/drafts/${draftId}/send`);
      return res.json();
    },
    onSuccess: () => {
      invalidateAfter(true);
      toast({ title: "Sent", description: "Your draft was sent." });
      onClose();
    },
    onError: (err: any) => toast({ title: "Send failed", description: err?.message, variant: "destructive" }),
  });

  const discardDraft = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", `/api/platform/gmail/drafts/${draftId}`);
      return res.json();
    },
    onSuccess: () => {
      invalidateAfter();
      toast({ title: "Draft discarded" });
      onClose();
    },
    onError: (err: any) => toast({ title: "Discard failed", description: err?.message, variant: "destructive" }),
  });

  if (detailQuery.isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const err = detailQuery.error as any;
  if (err?.message?.includes("not connected") || err?.status === 401 || err?.status === 403) {
    return (
      <div className="p-6 text-sm text-muted-foreground flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Connect your Gmail to edit this draft.</span>
      </div>
    );
  }
  if (err || !detailQuery.data?.draft) return <div className="p-6 text-sm text-destructive">Failed to load draft.</div>;

  const busy = saveDraft.isPending || sendDraft.isPending || discardDraft.isPending;

  return (
    <div className="flex flex-col h-full" data-testid="platform-draft-editor">
      <div className="px-6 py-4 border-b shrink-0">
        <div className="text-lg font-semibold">Edit draft</div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={`pt-draft-to-${draftId}`}>To</Label>
            <Input id={`pt-draft-to-${draftId}`} value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@example.com" data-testid="input-draft-to" />
          </div>
          {!showCcBcc ? (
            <button type="button" onClick={() => setShowCcBcc(true)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="button-show-cc-bcc">
              Add Cc/Bcc
            </button>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor={`pt-draft-cc-${draftId}`}>Cc</Label>
                <Input id={`pt-draft-cc-${draftId}`} value={cc} onChange={(e) => setCc(e.target.value)} data-testid="input-draft-cc" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`pt-draft-bcc-${draftId}`}>Bcc</Label>
                <Input id={`pt-draft-bcc-${draftId}`} value={bcc} onChange={(e) => setBcc(e.target.value)} data-testid="input-draft-bcc" />
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <Label htmlFor={`pt-draft-subject-${draftId}`}>Subject</Label>
            <Input id={`pt-draft-subject-${draftId}`} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="input-draft-subject" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`pt-draft-body-${draftId}`}>Body</Label>
            <VoiceToTextTextarea id={`pt-draft-body-${draftId}`} value={body} onChange={(e) => setBody(e.target.value)} rows={14} className="font-mono text-sm" data-testid="input-draft-body" />
          </div>
        </div>
      </ScrollArea>
      <div className="px-6 py-3 border-t shrink-0 flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => { if (window.confirm("Discard this draft?")) discardDraft.mutate(); }} disabled={busy} data-testid="button-draft-discard">
          <Trash2 className="w-4 h-4 mr-1" /> Discard
        </Button>
        <Button variant="outline" size="sm" onClick={() => saveDraft.mutate()} disabled={busy} data-testid="button-draft-save">
          {saveDraft.isPending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1" /> Save</>}
        </Button>
        <Button size="sm" onClick={() => sendDraft.mutate()} disabled={busy || !to.trim()} data-testid="button-draft-send">
          {sendDraft.isPending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-1" /> Send</>}
        </Button>
      </div>
    </div>
  );
}

// ─── Compose Panel ────────────────────────────────────────────────────────────

type ComposeAgent = {
  id: string;
  name: string;
  status: "active" | "paused" | "archived";
  emailIdentityType: "cold_mailbox" | "connected_gmail" | null;
  assignedEmail: string | null;
  backingUserId: string | null;
};

const SELF_IDENTITY = "__self__";

function ComposePanel() {
  const { toast } = useToast();
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showCc, setShowCc] = useState(false);
  // "Send as" identity. SELF_IDENTITY = the signed-in admin's own Gmail.
  const [sendAs, setSendAs] = useState<string>(SELF_IDENTITY);

  const agentsQuery = useQuery<ComposeAgent[]>({
    queryKey: ["/api/platform/ai-agents"],
  });
  // Only agents with a connected sending identity can actually send a 1:1
  // email; the rest are listed disabled so it's clear they need a mailbox.
  const agents = agentsQuery.data ?? [];

  // Shared mailboxes (e.g. sales@clarik.io) — selectable when send-connected.
  // Their select values are prefixed "mbx:" so the mutation can route them to
  // the sendAsSharedMailboxId param.
  const mailboxesQuery = useQuery<SharedMailbox[]>({
    queryKey: ["/api/platform/shared-mailboxes"],
  });
  const sharedMailboxes = mailboxesQuery.data ?? [];

  const sendMutation = useMutation({
    mutationFn: async (saveDraft: boolean) => {
      const isMbx = sendAs.startsWith("mbx:");
      const sendAsAgentId = sendAs === SELF_IDENTITY || isMbx ? null : sendAs;
      const sendAsSharedMailboxId = isMbx ? sendAs.slice(4) : null;
      const res = await apiRequest("POST", "/api/platform/gmail/compose", { to, cc, subject, body, saveDraft, sendAsAgentId, sendAsSharedMailboxId });
      return res.json();
    },
    onSuccess: (data: any, saveDraft) => {
      const asSuffix = data?.sentAs ? ` as ${data.sentAs}` : "";
      if (saveDraft) {
        toast({ title: "Saved to Drafts" });
        queryClient.invalidateQueries({ queryKey: ["/api/platform/gmail/drafts"] });
      } else {
        toast({ title: `Email sent${asSuffix}` });
        queryClient.invalidateQueries({ queryKey: ["/api/platform/gmail/sent"] });
      }
      setTo(""); setCc(""); setSubject(""); setBody("");
    },
    onError: (err: any) => {
      const msg = err?.message || "Could not send";
      const needsReconnect = err?.needsReconnect;
      toast({ title: "Failed", description: needsReconnect ? "Gmail not connected." : msg, variant: "destructive" });
    },
  });

  return (
    <Card className="flex flex-col h-full" data-testid="platform-compose-panel">
      <div className="px-6 py-4 border-b shrink-0">
        <div className="text-lg font-semibold">New Email</div>
        <div className="text-sm text-muted-foreground">Compose and send a new email via your Gmail account.</div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-6 space-y-4 max-w-2xl">
          <div className="space-y-1.5">
            <Label htmlFor="compose-send-as">Send as</Label>
            <Select value={sendAs} onValueChange={setSendAs}>
              <SelectTrigger id="compose-send-as" data-testid="select-compose-send-as">
                <SelectValue placeholder="Yourself (your Gmail)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SELF_IDENTITY} data-testid="option-send-as-self">
                  Yourself (your Gmail)
                </SelectItem>
                {agents.map((a) => {
                  const isGmail = a.emailIdentityType === "connected_gmail";
                  const sendable = a.status === "active" && isGmail && !!a.backingUserId;
                  const reason = !isGmail
                    ? " (cold-outreach only)"
                    : !a.backingUserId
                      ? " (no mailbox)"
                      : a.status !== "active"
                        ? ` (${a.status})`
                        : "";
                  return (
                    <SelectItem
                      key={a.id}
                      value={a.id}
                      disabled={!sendable}
                      data-testid={`option-send-as-${a.id}`}
                    >
                      {a.name}
                      {a.assignedEmail ? ` · ${a.assignedEmail}` : ""}
                      {reason}
                    </SelectItem>
                  );
                })}
                {sharedMailboxes.map((m) => {
                  const sendable = m.status === "active" && m.connected;
                  const reason = !m.connected
                    ? " (not connected)"
                    : m.status !== "active"
                      ? ` (${m.status})`
                      : "";
                  return (
                    <SelectItem
                      key={m.id}
                      value={`mbx:${m.id}`}
                      disabled={!sendable}
                      data-testid={`option-send-as-mbx-${m.id}`}
                    >
                      {m.label} · {m.email}
                      {reason}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {sendAs !== SELF_IDENTITY && (
              <p className="text-xs text-muted-foreground" data-testid="text-send-as-note">
                {sendAs.startsWith("mbx:")
                  ? "This 1:1 email will be sent from the selected shared mailbox."
                  : "This 1:1 email will be sent from the selected agent's connected mailbox."}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="compose-to">To</Label>
            <Input id="compose-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@example.com" data-testid="input-compose-to" />
          </div>
          {!showCc ? (
            <button type="button" onClick={() => setShowCc(true)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="button-compose-show-cc">
              Add Cc
            </button>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="compose-cc">Cc</Label>
              <Input id="compose-cc" value={cc} onChange={(e) => setCc(e.target.value)} data-testid="input-compose-cc" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="compose-subject">Subject</Label>
            <Input id="compose-subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="input-compose-subject" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="compose-body">Body</Label>
            <VoiceToTextTextarea id="compose-body" value={body} onChange={(e) => setBody(e.target.value)} rows={16} className="font-mono text-sm" placeholder="Write your message…" data-testid="input-compose-body" />
          </div>
        </div>
      </ScrollArea>
      <div className="px-6 py-3 border-t shrink-0 flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => sendMutation.mutate(true)}
          disabled={sendMutation.isPending || !to.trim()}
          data-testid="button-compose-save-draft"
        >
          {sendMutation.isPending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1" /> Save Draft</>}
        </Button>
        <Button
          size="sm"
          onClick={() => sendMutation.mutate(false)}
          disabled={sendMutation.isPending || !to.trim()}
          data-testid="button-compose-send"
        >
          {sendMutation.isPending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-1" /> Send</>}
        </Button>
      </div>
    </Card>
  );
}

// ─── Shared Mailboxes Panel ───────────────────────────────────────────────────

type SharedMailbox = {
  id: string;
  email: string;
  label: string;
  backingUserId: string;
  readEnabled: boolean;
  status: "active" | "paused";
  connected: boolean;
  connectedWrongAccount?: boolean;
  connectedEmail: string | null;
};

function SharedMailboxesPanel() {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");

  const mailboxesQuery = useQuery<SharedMailbox[]>({
    queryKey: ["/api/platform/shared-mailboxes"],
  });
  const mailboxes = mailboxesQuery.data ?? [];

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/platform/shared-mailboxes", { email, label });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Shared mailbox added" });
      setEmail(""); setLabel("");
      queryClient.invalidateQueries({ queryKey: ["/api/platform/shared-mailboxes"] });
    },
    onError: (err: any) => {
      toast({ title: "Could not add mailbox", description: err?.message, variant: "destructive" });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/platform/shared-mailboxes/${id}/disconnect`);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Mailbox disconnected" });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/shared-mailboxes"] });
    },
    onError: (err: any) => {
      toast({ title: "Could not disconnect", description: err?.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/platform/shared-mailboxes/${id}`);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Shared mailbox removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/shared-mailboxes"] });
    },
    onError: (err: any) => {
      toast({ title: "Could not remove mailbox", description: err?.message, variant: "destructive" });
    },
  });

  const handleConnect = async (id: string) => {
    try {
      const res = await apiRequest("GET", `/api/platform/shared-mailboxes/${id}/connect?returnUrl=${encodeURIComponent("/platform/email?folder=mailboxes")}`);
      const data = await res.json();
      if (data.authUrl) window.location.href = data.authUrl;
    } catch (err: any) {
      toast({ title: "Could not start connection", description: err?.message, variant: "destructive" });
    }
  };

  return (
    <Card className="flex flex-col h-full" data-testid="platform-shared-mailboxes-panel">
      <div className="px-6 py-4 border-b shrink-0">
        <div className="text-lg font-semibold">Shared Mailboxes</div>
        <div className="text-sm text-muted-foreground">
          Provision shared addresses (e.g. sales@clarik.io) as <span className="font-medium">send-only</span> identities.
          They become available in the Compose “Send as” selector. Shared-inbox reading is not enabled yet.
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-6 space-y-6 max-w-2xl">
          {/* Add new */}
          <div className="rounded-lg border p-4 space-y-3" data-testid="shared-mailbox-add">
            <div className="text-sm font-medium">Add a shared mailbox</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="mbx-label">Label</Label>
                <Input id="mbx-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Sales" data-testid="input-mailbox-label" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mbx-email">Address</Label>
                <Input id="mbx-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="sales@clarik.io" data-testid="input-mailbox-email" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending || !email.trim() || !label.trim()}
                data-testid="button-add-mailbox"
              >
                {createMutation.isPending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Adding…</> : <><Plus className="w-4 h-4 mr-1" /> Add Mailbox</>}
              </Button>
            </div>
          </div>

          {/* List */}
          {mailboxesQuery.isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : mailboxes.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8" data-testid="text-no-mailboxes">
              No shared mailboxes yet. Add one above.
            </div>
          ) : (
            <div className="space-y-3">
              {mailboxes.map((m) => (
                <div
                  key={m.id}
                  className="rounded-lg border p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                  data-testid={`row-mailbox-${m.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium flex items-center gap-2">
                      {m.label}
                      {m.connected ? (
                        <Badge variant="secondary" className="shrink-0" data-testid={`badge-mailbox-connected-${m.id}`}>
                          <MailOpen className="w-3.5 h-3.5 mr-1" /> Send connected
                        </Badge>
                      ) : m.connectedWrongAccount ? (
                        <Badge variant="destructive" className="shrink-0" data-testid={`badge-mailbox-wrong-account-${m.id}`}>
                          <AlertCircle className="w-3.5 h-3.5 mr-1" /> Wrong account
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="shrink-0" data-testid={`badge-mailbox-disconnected-${m.id}`}>
                          Not connected
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">
                      {m.email}
                      {m.connectedWrongAccount && m.connectedEmail ? (
                        <span className="text-destructive"> · connected to {m.connectedEmail}; reconnect with the correct account</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {m.connected ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => disconnectMutation.mutate(m.id)}
                        disabled={disconnectMutation.isPending}
                        data-testid={`button-disconnect-mailbox-${m.id}`}
                      >
                        <Unlink className="w-4 h-4 mr-1" /> Disconnect
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleConnect(m.id)}
                        data-testid={`button-connect-mailbox-${m.id}`}
                      >
                        <Link2 className="w-4 h-4 mr-1" /> Connect (send)
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (window.confirm(`Remove shared mailbox "${m.label}" (${m.email})? This also disconnects its sending access.`)) {
                          deleteMutation.mutate(m.id);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                      data-testid={`button-delete-mailbox-${m.id}`}
                      aria-label="Remove mailbox"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </Card>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function readInitialFolder(): Folder {
  if (typeof window === "undefined") return "inbox";
  const q = new URLSearchParams(window.location.search).get("folder");
  return q === "drafts" || q === "sent" || q === "compose" || q === "mailboxes" ? q : "inbox";
}

export default function PlatformEmailHub() {
  const { toast } = useToast();
  const [folder, setFolder] = useState<Folder>(readInitialFolder);

  const statusQuery = useQuery<GmailStatus>({
    queryKey: ["/api/platform/gmail/status"],
    retry: false,
  });

  const connected = statusQuery.data?.connected ?? false;
  const connectedEmail = statusQuery.data?.email || null;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (folder === "inbox") {
      url.searchParams.delete("folder");
    } else {
      url.searchParams.set("folder", folder);
    }
    window.history.replaceState({}, "", url.toString());
  }, [folder]);

  const handleConnect = async () => {
    // Use raw fetch (GET needs no CSRF token) so we can inspect the HTTP
    // status and route a lapsed platform session through re-login instead of
    // showing a dead-end toast.
    const res = await fetch(`/api/platform/gmail/auth?returnUrl=/platform/email`, {
      credentials: "include",
    });
    if (!res.ok) {
      if (res.status === 401) {
        const returnTo = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.href = `/api/login?silent=1&returnTo=${returnTo}`;
        return;
      }
      const body = await res.json().catch(() => ({}));
      toast({ title: "Could not initiate Gmail auth", description: body?.message, variant: "destructive" });
      return;
    }
    const data = await res.json();
    if (data.authUrl) window.location.href = data.authUrl;
  };

  return (
    <PlatformLayout fullBleed>
    <div className="flex flex-col h-full min-h-0 overflow-hidden" data-testid="page-platform-email-hub">
      <div className="shrink-0 px-4 sm:px-6 pt-4 sm:pt-6 space-y-3">
        <div className="flex items-center gap-3">
          <Mail className="w-6 h-6" />
          <div className="flex-1">
            <h1 className="text-2xl font-semibold">Email Hub</h1>
            <p className="text-sm text-muted-foreground">
              {connected
                ? `Connected as ${connectedEmail || "your Gmail account"}`
                : "Your personal Gmail inbox, drafts, sent, and compose — all in one place."}
            </p>
          </div>
          {connected && (
            <Badge variant="secondary" className="shrink-0" data-testid="badge-gmail-connected">
              <MailOpen className="w-3.5 h-3.5 mr-1" />
              Gmail connected
            </Badge>
          )}
          {!connected && !statusQuery.isLoading && (
            <Button size="sm" onClick={handleConnect} data-testid="button-connect-gmail-header">
              Connect Gmail
            </Button>
          )}
        </div>

        <div className="flex items-center gap-1 border-b" data-testid="email-hub-folder-tabs">
          {FOLDERS.map(({ key, label, icon: Icon }) => {
            const active = folder === key;
            return (
              <button
                key={key}
                onClick={() => setFolder(key)}
                data-testid={`rail-${key}`}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px",
                  active
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
                )}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden px-4 sm:px-6 py-4">
        {/* Shared mailbox management is independent of the admin's own Gmail
            connection, so it renders before the personal-connect gate. */}
        {folder === "mailboxes" ? (
          <SharedMailboxesPanel />
        ) : statusQuery.isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : !connected ? (
          <GmailConnectPrompt onConnect={handleConnect} />
        ) : (
          <>
            {folder === "inbox" && <InboxPanel />}
            {folder === "drafts" && <GmailFolderPanel folder="drafts" />}
            {folder === "sent" && <GmailFolderPanel folder="sent" />}
            {folder === "compose" && <ComposePanel />}
          </>
        )}
      </div>
    </div>
    </PlatformLayout>
  );
}
